import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import chiaraImage from '../assets/chiara.jpg';
import marinetteImg from '../assets/marinette-logo.png';
import borrowedTimeImg from '../assets/borrowed-time-logo.png';
import lolImg from '../assets/lol-logo.png';
import moustikImg from '../assets/moustik-logo.png';
import lavoirImg from '../assets/lavoir-logo.jpg';
import ProjectDetail from './ProjectDetail';
import { projectsData } from '../data/projectsData';

// --- INTERFACES (TYPESCRIPT) ---
interface Project {
  id: string;
  folderName: string;
  title: string;
  date: string;
  description: string;
  fullDescription?: string;
  images?: any;
  logo?: string;
}

// --- DONNÉES DU PORTFOLIO ---
const portfolioData = {
  name: "Chiara COMBLEZ",
  title: "Habilleuse & Costumière",
  about: `Depuis toujours, je suis fascinée par la manière dont un vêtement peut raconter une histoire. Cette passion m'a naturellement menée vers la mode et le costume, où j'aime concevoir et manipuler les pièces qui donnent vie aux personnages, aussi bien en création qu'en habillage.

Après un bachelor en stylisme et modélisme et plusieurs expériences sur des tournages en tant qu'habilleuse et assistante costumière, je poursuis aujourd'hui mes études en deuxième année de licence en études théâtrales à la Sorbonne, avec l'objectif d'intégrer la licence professionnelle costume en troisième année.

J'accorde une importance particulière aux détails, aux matières et à la cohérence visuelle d'un personnage. À travers mon travail, je cherche à allier sens artistique et rigueur technique pour contribuer à la construction visuelle et émotionnelle d'un projet.`,
  
  contact: {
    phone: "07.83.02.83.27",
    email: "chiaracomblez@gmail.com",
    linkedin: "https://www.linkedin.com/in/chiara-comblez-42a526254/",
    instagram: "https://www.instagram.com/cchhiiaarraa_c/",
  },

  projects: [
    {
      id: "les-femmes-du-lavoir",
      folderName: "Les femmes du lavoir",
      title: "Les femmes du lavoir",
      date: "2026",
      description: "Costumes de Scène • Histoire & Émancipation",
      logo: lavoirImg,
    },
    {
      id: "borrowed-time",
      folderName: "Borrowed Time",
      title: "Borrowed Time",
      date: "2024",
      description: "Défilé engagé • Liberté et identité",
      logo: borrowedTimeImg,
    },
    {
      id: "moustik",
      folderName: "Moustik",
      title: "Moustik",
      date: "2024",
      description: "Haute Couture • Collection Automne/Hiver 2025",
      logo: moustikImg,
    },
    {
      id: "chemistry-off-white-league-of-legends",
      folderName: "Chemistry LOL",
      title: "Off-White x League of Legends",
      date: "2024",
      description: "Collaboration • Mode et Gaming",
      logo: lolImg,
    },
    {
      id: "marinette",
      folderName: "Marinette",
      title: "Marinette",
      date: "2024",
      description: "Collection Maritime • Hommage aux femmes de la mer",
      logo: marinetteImg,
    },
  ] as Project[],
};

// --- COMPOSANTS ---

const Header: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <header style={{
      ...styles.header,
      backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0.85)',
      boxShadow: scrolled ? '0 2px 20px rgba(233, 30, 140, 0.08)' : 'none',
    }}>
      <div style={styles.headerTitle}>{portfolioData.name}</div>
      <nav style={styles.nav} className="desktop-nav">
        <a href="#about" style={styles.navLink}>À Propos</a>
        <a href="#projects" style={styles.navLink}>Projets</a>
        <a href="#contact" style={styles.navLink}>Contact</a>
      </nav>
      <div style={styles.hamburger} onClick={toggleMenu} className="mobile-nav">
        <div style={{...styles.hamburgerLine, ...(isMenuOpen ? styles.hamburgerLineOpen1 : {})}}></div>
        <div style={{...styles.hamburgerLine, ...(isMenuOpen ? styles.hamburgerLineOpen2 : {})}}></div>
        <div style={{...styles.hamburgerLine, ...(isMenuOpen ? styles.hamburgerLineOpen3 : {})}}></div>
      </div>
      {isMenuOpen && (
        <div style={styles.mobileMenu}>
          <a href="#about" style={styles.mobileNavLink} onClick={toggleMenu}>À Propos</a>
          <a href="#projects" style={styles.mobileNavLink} onClick={toggleMenu}>Projets</a>
          <a href="#contact" style={styles.mobileNavLink} onClick={toggleMenu}>Contact</a>
        </div>
      )}
    </header>
  );
};

const Hero: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  return (
    <section style={{
      ...styles.hero,
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
    }}>
      <div style={styles.heroPattern}></div>
      <div style={styles.heroContent}>
        <div style={styles.heroLogo}>
          <div role="img" aria-label="Logo Chiara Comblez" style={styles.logoMasked}></div>
        </div>
        <h1 style={styles.heroName}>{portfolioData.name}</h1>
        <div style={styles.heroDivider}></div>
        <h2 style={styles.heroTitle}>{portfolioData.title}</h2>
      </div>
    </section>
  );
};

const About: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="about" 
      ref={sectionRef}
      style={{
        ...styles.section,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease, transform 0.8s ease',
      }}
    >
      <h2 style={styles.sectionTitle}>
        <span style={styles.titleOrange}>À propos de moi</span>
      </h2>
      <div style={styles.aboutContentWrapper} data-layout="about-grid">
        <div style={styles.aboutImage}>
          <img 
            src={chiaraImage} 
            alt="Chiara Comblez" 
            style={styles.aboutImageImg}
          />
        </div>
        <div style={styles.aboutContent}>
          {portfolioData.about.split('\n\n').map((paragraph, index) => (
            <p key={index} style={styles.paragraph}>{paragraph}</p>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProjectCard: React.FC<{ project: Project; index: number }> = ({ project, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 100);
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [index]);

  return (
    <div
      ref={cardRef}
      onClick={() => navigate(`/project/${project.id}`)}
      style={{
        ...styles.projectCard,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        boxShadow: isHovered ? '0 12px 40px rgba(233, 30, 140, 0.15)' : '0 4px 20px rgba(233, 30, 140, 0.06)',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {project.logo && (
        <div style={styles.projectLogoContainer}>
          <img src={project.logo} alt={project.title} style={styles.projectLogo} />
        </div>
      )}
      <div style={styles.projectCardContentSimple}>
        <h3 style={styles.projectTitleSimple}>{project.title}</h3>
        <p style={styles.projectDateSimple}>{project.date}</p>
      </div>
    </div>
  );
};

const Projects: React.FC = () => (
  <section id="projects" style={styles.section}>
    <h2 style={styles.sectionTitle}>Projets</h2>
    <div style={styles.projectGrid}>
      {portfolioData.projects.map((project, index) => (
        <ProjectCard 
          key={index} 
          project={project} 
          index={index}
        />
      ))}
    </div>
  </section>
);

const Contact: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      id="contact" 
      ref={sectionRef}
      style={{
        ...styles.contactSection,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease, transform 0.8s ease',
      }}
    >
      <div style={styles.contactContainer}>
        <h2 style={styles.contactTitle}>Contactez-moi</h2>
        <div style={styles.contactInfo}>
          <div style={styles.contactItem}>
            <span style={styles.contactLabel}>Tél :</span>
            <a href={`tel:${portfolioData.contact.phone.replace(/\./g, '')}`} style={styles.contactLink}>
              {portfolioData.contact.phone}
            </a>
          </div>
          <div style={styles.contactItem}>
            <span style={styles.contactLabel}>Email :</span>
            <a href={`mailto:${portfolioData.contact.email}`} style={styles.contactLink}>
              {portfolioData.contact.email}
            </a>
          </div>
        </div>
        <div style={styles.socialLinks}>
          <SocialButton href={portfolioData.contact.linkedin}>LinkedIn</SocialButton>
          {portfolioData.contact.instagram && (
            <SocialButton href={portfolioData.contact.instagram}>Instagram</SocialButton>
          )}
        </div>
      </div>
    </section>
  );
};

const SocialButton: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <a 
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...styles.socialButton,
        backgroundColor: isHovered ? '#FFFFFF' : 'transparent',
        color: isHovered ? '#F5A347' : '#FFFFFF',
        transform: isHovered ? 'translateY(-3px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}
    </a>
  );
};

const Footer: React.FC = () => (
  <footer style={styles.footer}>
    <div style={styles.footerContent}>
      <p style={styles.footerText}>© {new Date().getFullYear()} {portfolioData.name}</p>
      <p style={styles.footerSubtext}>Styliste & Costumière</p>
    </div>
  </footer>
);

// --- PAGE PRINCIPALE ---
const HomePage: React.FC = () => {
  return (
    <>
      <Header />
      <main>
        <Hero />
        <About />
        <Projects />
        <Contact />
      </main>
      <Footer />
    </>
  );
};

function App() {
  useEffect(() => {
    // Inject Google Fonts - Style similaire à la carte
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Montserrat:wght@300;400;500;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Global styles
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `
      * {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
      }
      body {
        margin: 0;
        overflow-x: hidden;
      }
      ::-webkit-scrollbar {
        width: 8px;
      }
      ::-webkit-scrollbar-track {
        background: #F5F5F5;
      }
      ::-webkit-scrollbar-thumb {
        background: #CCCCCC;
        border-radius: 4px;
      }
      ::-webkit-scrollbar-thumb:hover {
        background: #999999;
      }
      @media (max-width: 768px) {
        [data-layout="about-grid"] {
          grid-template-columns: 1fr !important;
        }
        .desktop-nav {
          display: none !important;
        }
        .mobile-nav {
          display: flex !important;
        }
      }
    `;
    document.head.appendChild(styleTag);

    return () => {
      document.head.removeChild(link);
      document.head.removeChild(styleTag);
    };
  }, []);

  return (
    <Router>
      <div style={styles.main}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/project/:projectId" element={<ProjectDetail />} />
        </Routes>
      </div>
    </Router>
  );
}

// --- STYLES (CSS-in-JS) ---
const styles: { [key: string]: React.CSSProperties } = {
  main: {
    fontFamily: "'Montserrat', -apple-system, BlinkMacSystemFont, sans-serif",
    color: '#E91E8C',
    backgroundColor: '#FFFFFF',
    lineHeight: 1.7,
  },
  header: {
    padding: '1.5rem 5%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    position: 'fixed',
    width: '90%',
    top: 0,
    zIndex: 100,
    backdropFilter: 'blur(15px)',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  headerTitle: {
    fontFamily: "'Playfair Display', serif",
    fontWeight: 700,
    fontSize: '1.5rem',
    color: '#E91E8C',
    letterSpacing: '0.02em',
  },
  nav: {
    display: 'flex',
    gap: '2.5rem',
  },
  navLink: {
    textDecoration: 'none',
    color: '#E91E8C',
    fontWeight: 500,
    fontSize: '0.95rem',
    transition: 'color 0.3s ease',
    position: 'relative',
  },
  hamburger: {
    display: 'none',
    flexDirection: 'column',
    justifyContent: 'space-around',
    width: '2rem',
    height: '2rem',
    cursor: 'pointer',
    zIndex: 101,
  },
  hamburgerLine: {
    width: '2rem',
    height: '0.25rem',
    backgroundColor: '#E91E8C',
    borderRadius: '10px',
    transition: 'all 0.3s linear',
    position: 'relative',
    transformOrigin: '1px',
  },
  hamburgerLineOpen1: {
    transform: 'rotate(45deg)',
  },
  hamburgerLineOpen2: {
    opacity: 0,
    transform: 'translateX(20px)',
  },
  hamburgerLineOpen3: {
    transform: 'rotate(-45deg)',
  },
  mobileMenu: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100vh',
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  },
  mobileNavLink: {
    textDecoration: 'none',
    color: '#E91E8C',
    fontWeight: 600,
    fontSize: '1.5rem',
    marginBottom: '2rem',
    transition: 'color 0.3s ease',
  },
  hero: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    paddingTop: '100px',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    overflow: 'hidden',
    transition: 'opacity 1s ease, transform 1s ease',
  },
  heroPattern: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: 'none',
    pointerEvents: 'none',
  },
  heroContent: {
    maxWidth: '900px',
    padding: '0 2rem',
    position: 'relative',
    zIndex: 1,
  },
  heroLogo: {
    width: '120px',
    height: '120px',
    margin: '0 auto 2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoMasked: {
    width: '100%',
    height: '100%',
    backgroundColor: '#E91E8C',
    WebkitMaskImage: `url(${logo})`,
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskSize: 'contain',
    WebkitMaskPosition: 'center',
    maskImage: `url(${logo})`,
    maskRepeat: 'no-repeat',
    maskSize: 'contain',
    maskPosition: 'center',
    filter: 'drop-shadow(0 4px 20px rgba(233, 30, 140, 0.2))',
  },
  heroName: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(3rem, 10vw, 6rem)',
    margin: 0,
    fontWeight: 700,
    color: '#E91E8C',
    lineHeight: 1.1,
    textShadow: '0 2px 30px rgba(0, 0, 0, 0.1)',
  },
  heroDivider: {
    width: '100px',
    height: '2px',
    backgroundColor: '#E91E8C',
    margin: '2.5rem auto',
    opacity: 0.8,
  },
  heroTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
    margin: 0,
    fontWeight: 300,
    letterSpacing: '0.1em',
    color: '#E91E8C',
    textTransform: 'uppercase',
  },
  section: {
    padding: '6rem 5%',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  sectionTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: '4rem',
    color: '#E91E8C',
  },
  titleItalic: {
    fontStyle: 'italic',
    fontWeight: 400,
    color: '#E91E8C',
  },
  aboutContent: {
    textAlign: 'left',
  },
  paragraph: {
    fontSize: '1.05rem',
    color: '#3D3D3D',
    marginBottom: '1.5rem',
    lineHeight: 1.8,
    fontWeight: 400,
  },
  projectGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '1.5rem',
    marginTop: '2rem',
  },
  projectCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column' as const,
    border: '2px solid #F8E1F0',
    minHeight: '150px',
  },
  projectImageContainer: {
    width: '100%',
    height: '320px',
    overflow: 'hidden',
    position: 'relative',
    backgroundColor: '#F8E1F0',
  },
  projectImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  projectOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(233, 30, 140, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.4s ease',
    pointerEvents: 'none',
  },
  viewMore: {
    color: '#FFFFFF',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '1rem',
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    fontWeight: 600,
  },
  projectCardContent: {
    padding: '2rem',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
  },
  projectTitle: {
    margin: '0 0 0.8rem 0',
    fontSize: '1.6rem',
    fontFamily: "'Playfair Display', serif",
    fontWeight: 700,
    color: '#E91E8C',
  },
  projectRole: {
    margin: '0 0 1.5rem 0',
    color: '#F5A347',
    fontStyle: 'italic',
    fontSize: '1rem',
    fontWeight: 500,
  },
  projectDescription: {
    margin: '0 0 2rem 0',
    flexGrow: 1,
    color: '#5A5A5A',
    lineHeight: 1.7,
    fontWeight: 400,
  },
  projectTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.6rem',
    marginTop: 'auto',
  },
  projectTag: {
    backgroundColor: '#F8E1F0',
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    fontSize: '0.8rem',
    color: '#E91E8C',
    fontWeight: 500,
    border: '1px solid #E91E8C',
  },
  contactSection: {
    padding: '0',
    margin: '0',
    backgroundColor: '#F5A347',
  },
  contactContainer: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '6rem 5%',
    textAlign: 'center',
  },
  contactTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
    fontWeight: 700,
    marginBottom: '3rem',
    color: '#FFFFFF',
  },
  contactInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    marginBottom: '3rem',
    alignItems: 'center',
  },
  contactItem: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center',
    fontSize: '1.2rem',
  },
  contactLabel: {
    fontWeight: 600,
    color: '#FFFFFF',
  },
  contactLink: {
    color: '#FFFFFF',
    textDecoration: 'none',
    fontWeight: 400,
    transition: 'opacity 0.3s ease',
  },
  socialLinks: {
    display: 'flex',
    justifyContent: 'center',
    gap: '1.5rem',
    flexWrap: 'wrap',
    marginTop: '3rem',
  },
  socialButton: {
    display: 'inline-block',
    padding: '1rem 2.5rem',
    textDecoration: 'none',
    borderRadius: '30px',
    fontWeight: 600,
    fontSize: '0.95rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '2px solid #FFFFFF',
  },
  footer: {
    textAlign: 'center',
    padding: '3rem 5%',
    backgroundColor: '#000000',
    color: '#FFFFFF',
  },
  footerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
  },
  footerText: {
    fontSize: '1rem',
    fontWeight: 500,
    marginBottom: '0.5rem',
  },
  footerSubtext: {
    fontSize: '0.9rem',
    color: '#F8E1F0',
    fontWeight: 300,
    fontStyle: 'italic',
  },
  titleOrange: {
    color: '#F5A347',
  },
  aboutContentWrapper: {
    display: 'grid' as const,
    gridTemplateColumns: '1fr 2fr',
    gap: '3rem',
    alignItems: 'center',
  },
  aboutImage: {
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 8px 30px rgba(233, 30, 140, 0.15)',
    maxWidth: '300px',
  },
  aboutImageImg: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: '12px',
  },
  projectLogoContainer: {
    width: '100%',
    height: '180px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8E1F0',
    borderBottom: '2px solid #F8E1F0',
  },
  projectLogo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  },
  projectCardContentSimple: {
    padding: '2.5rem 2rem',
    textAlign: 'center' as const,
  },
  projectTitleSimple: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.8rem',
    fontWeight: 700,
    color: '#E91E8C',
    margin: 0,
    marginBottom: '0.8rem',
  },
  projectDateSimple: {
    color: '#F5A347',
    fontSize: '1.1rem',
    fontWeight: 500,
    margin: 0,
  },
};

export default App;